import React from "react";

import Box from "@mui/material/Box";
import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";

import { createTheme } from "@mui/material/styles";
import { ukUA } from "@mui/material/locale";

import { ReactComponent as ExpandIcon } from "assets/images/expand-icon.svg";

import { ReactComponent as CheckboxBorderIcon } from "assets/images/checkbox-border-icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "assets/images/checkbox-checked-icon.svg";

import { ReactComponent as AlertErrorIcon } from "assets/images/alert/alert-error-icon.svg";
import { ReactComponent as AlertSuccessIcon } from "assets/images/alert/alert-success-icon.svg";

const SelectExpandIcon = (props: SvgIconProps) => {
  return (
    <SvgIcon {...props}>
      <ExpandIcon />
    </SvgIcon>
  );
};

declare module "@mui/material/styles" {
  interface Palette {
    blue600: string;
    grey000: string;
    grey100: string;
    grey400: string;
    grey700: string;
    grey800: string;
    grey900: string;
    red700: string;
    red800: string;
    green300: string;
    green800: string;
  }

  interface PaletteOptions {
    blue600?: string;
    grey000?: string;
    grey100?: string;
    grey400?: string;
    grey700?: string;
    grey800?: string;
    grey900?: string;
    red700?: string;
    red800?: string;
    green300?: string;
    green800?: string;
  }

  interface Theme {
    colors: {
      // GREY
      grey000: React.CSSProperties["color"];
      grey100: React.CSSProperties["color"];
      grey200: React.CSSProperties["color"];
      grey300: React.CSSProperties["color"];
      grey400: React.CSSProperties["color"];
      grey500: React.CSSProperties["color"];
      grey600: React.CSSProperties["color"];
      grey700: React.CSSProperties["color"];
      grey800: React.CSSProperties["color"];
      grey900: React.CSSProperties["color"];

      // BLUE
      blue050: React.CSSProperties["color"];
      blue100: React.CSSProperties["color"];
      blue200: React.CSSProperties["color"];
      blue300: React.CSSProperties["color"];
      blue400: React.CSSProperties["color"];
      blue500: React.CSSProperties["color"];
      blue600: React.CSSProperties["color"];
      blue700: React.CSSProperties["color"];
      blue800: React.CSSProperties["color"];
      blue900: React.CSSProperties["color"];

      // GREEN
      green050: React.CSSProperties["color"];
      green100: React.CSSProperties["color"];
      green200: React.CSSProperties["color"];
      green300: React.CSSProperties["color"];
      green400: React.CSSProperties["color"];
      green500: React.CSSProperties["color"];
      green600: React.CSSProperties["color"];
      green700: React.CSSProperties["color"];
      green800: React.CSSProperties["color"];
      green900: React.CSSProperties["color"];

      // RED
      red050: React.CSSProperties["color"];
      red100: React.CSSProperties["color"];
      red200: React.CSSProperties["color"];
      red300: React.CSSProperties["color"];
      red400: React.CSSProperties["color"];
      red500: React.CSSProperties["color"];
      red600: React.CSSProperties["color"];
      red700: React.CSSProperties["color"];
      red800: React.CSSProperties["color"];
      red900: React.CSSProperties["color"];

      // YELLOW
      yellow050: React.CSSProperties["color"];
      yellow100: React.CSSProperties["color"];
      yellow200: React.CSSProperties["color"];
      yellow300: React.CSSProperties["color"];
      yellow400: React.CSSProperties["color"];
      yellow500: React.CSSProperties["color"];
      yellow600: React.CSSProperties["color"];
      yellow700: React.CSSProperties["color"];
      yellow800: React.CSSProperties["color"];
      yellow900: React.CSSProperties["color"];
    };
  }

  interface ThemeOptions {
    colors: {
      // GREY
      grey000: React.CSSProperties["color"];
      grey100: React.CSSProperties["color"];
      grey200: React.CSSProperties["color"];
      grey300: React.CSSProperties["color"];
      grey400: React.CSSProperties["color"];
      grey500: React.CSSProperties["color"];
      grey600: React.CSSProperties["color"];
      grey700: React.CSSProperties["color"];
      grey800: React.CSSProperties["color"];
      grey900: React.CSSProperties["color"];

      // BLUE
      blue050: React.CSSProperties["color"];
      blue100: React.CSSProperties["color"];
      blue200: React.CSSProperties["color"];
      blue300: React.CSSProperties["color"];
      blue400: React.CSSProperties["color"];
      blue500: React.CSSProperties["color"];
      blue600: React.CSSProperties["color"];
      blue700: React.CSSProperties["color"];
      blue800: React.CSSProperties["color"];
      blue900: React.CSSProperties["color"];

      // GREEN
      green050: React.CSSProperties["color"];
      green100: React.CSSProperties["color"];
      green200: React.CSSProperties["color"];
      green300: React.CSSProperties["color"];
      green400: React.CSSProperties["color"];
      green500: React.CSSProperties["color"];
      green600: React.CSSProperties["color"];
      green700: React.CSSProperties["color"];
      green800: React.CSSProperties["color"];
      green900: React.CSSProperties["color"];

      // RED
      red050: React.CSSProperties["color"];
      red100: React.CSSProperties["color"];
      red200: React.CSSProperties["color"];
      red300: React.CSSProperties["color"];
      red400: React.CSSProperties["color"];
      red500: React.CSSProperties["color"];
      red600: React.CSSProperties["color"];
      red700: React.CSSProperties["color"];
      red800: React.CSSProperties["color"];
      red900: React.CSSProperties["color"];

      // YELLOW
      yellow050: React.CSSProperties["color"];
      yellow100: React.CSSProperties["color"];
      yellow200: React.CSSProperties["color"];
      yellow300: React.CSSProperties["color"];
      yellow400: React.CSSProperties["color"];
      yellow500: React.CSSProperties["color"];
      yellow600: React.CSSProperties["color"];
      yellow700: React.CSSProperties["color"];
      yellow800: React.CSSProperties["color"];
      yellow900: React.CSSProperties["color"];
    };
  }
}

let theme = createTheme(
  {
    palette: {
      primary: {
        main: "#0000FF",
        light: "#CBDCFF",
        dark: "#00002E",
      },
      success: {
        main: "#3FA93A",
        light: "#C9FFC7",
        dark: "#2D7129",
      },
      error: {
        main: "#FF6E3D",
        light: "#FFF7F6",
        dark: "#AA2E00",
      },
      warning: {
        main: "#CFC52C",
        light: "#FFFFDC",
        dark: "#343322",
      },
      text: {
        primary: "#000000",
        secondary: "#393939",
      },

      blue600: "#0000FF",
      grey000: "#FFFFFF",
      grey100: "#FAFAFA",
      grey400: "#E5E5E5",
      grey700: "#747474",
      grey800: "#393939",
      grey900: "#000000",
      red700: "#FF4500",
      red800: "#AA2E00",
      green300: "#C9FFC7",
      green800: "#2D7129",
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1370,
        xl: 1536,
      },
    },
    colors: {
      // GREY
      grey000: "#FFFFFF",
      grey100: "#FAFAFA",
      grey200: "#F5F5F5",
      grey300: "#EDEDED",
      grey400: "#E5E5E5",
      grey500: "#CDCDCD",
      grey600: "#A8A8A8",
      grey700: "#747474",
      grey800: "#393939",
      grey900: "#000000",

      // BLUE
      blue050: "#F4FBFF",
      blue100: "#E5F1FF",
      blue200: "#CBDCFF",
      blue300: "#AFC1FF",
      blue400: "#798AFF",
      blue500: "#3C46FF",
      blue600: "#0000FF",
      blue700: "#0404AC",
      blue800: "#020267",
      blue900: "#00002E",

      // GREEN
      green050: "#F4FFF4",
      green100: "#E9FFE8",
      green200: "#D8FFD8",
      green300: "#C9FFC7",
      green400: "#A6FFA3",
      green500: "#7DF178",
      green600: "#51DA4B",
      green700: "#3FA93A",
      green800: "#2D7129",
      green900: "#183718",

      // RED
      red050: "#FFF7F6",
      red100: "#FFEDEC",
      red200: "#FFDEDB",
      red300: "#FFD3C9",
      red400: "#FFB7A4",
      red500: "#FF9574",
      red600: "#FF6E3D",
      red700: "#FF4500",
      red800: "#AA2E00",
      red900: "#541700",

      // YELLOW
      yellow050: "#ffffdc",
      yellow100: "#ffffb6",
      yellow200: "#ffff90",
      yellow300: "#ffff67",
      yellow400: "#fff63a",
      yellow500: "#f4e70b",
      yellow600: "#cfc52c",
      yellow700: "#a19a3a",
      yellow800: "#6c6836",
      yellow900: "#343322",
    },
    typography: {
      fontFamily: "Inter, sans-serif",
      h1: {
        fontFamily: "Inter, sans-serif",
        "@media (max-width:600px)": {
          fontSize: "48px",
          lineHeight: "56px",
        },
        "@media (min-width:600px)": {
          fontSize: "60px",
          lineHeight: "68px",
        },
        letterSpacing: "-0.8px",
        fontWeight: "600",
      },
      h2: {
        fontFamily: "Inter, sans-serif",
        "@media (max-width:600px)": {
          fontSize: "36px",
          lineHeight: "42px",
        },
        "@media (min-width:600px)": {
          fontSize: "48px",
          lineHeight: "56px",
        },
        letterSpacing: "-0.8px",
        fontWeight: "400",
      },
      h3: {
        fontFamily: "Inter, sans-serif",
        fontSize: "24px",
        lineHeight: "28px",
        letterSpacing: "0",
        fontWeight: "400",
      },
      h4: {
        fontFamily: "Inter, sans-serif",
        fontSize: "22px",
        lineHeight: "26px",
        letterSpacing: "0",
        fontWeight: "500",
      },
      h5: {
        fontFamily: "Inter, sans-serif",
        fontSize: "20px",
        fontWeight: "400",
        lineHeight: "28px",
        letterSpacing: "0",
      },
      body1: {
        fontFamily: "Inter, sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        lineHeight: "24px",
        letterSpacing: "0",
      },
      body2: {
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
        fontWeight: "400",
        lineHeight: "20px",
        letterSpacing: "0",
      },
      subtitle1: {
        fontFamily: "Inter, sans-serif",
        textTransform: "uppercase",
        fontSize: "12px",
        fontWeight: "600",
        lineHeight: "14px",
        letterSpacing: "2px",
      },
      subtitle2: {
        fontFamily: "Inter, sans-serif",
        textDecorationColor: "inherit",
        fontSize: "12px",
        lineHeight: "14px",
        fontWeight: 400,
        letterSpacing: "0px",
      },
      button: {
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "24px",
        letterSpacing: "0px",
        textTransform: "none",
      },
    },
  },
  ukUA,
);

theme = createTheme(theme, {
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          color: theme.colors.grey900,
          borderRadius: "0px",
          boxShadow: "none",
          "@media (max-width:600px)": {
            padding: "16px",
          },
          "@media (min-width:600px)": {
            padding: "18px 20px",
          },
        },
      },
    },
    // TODO: remove paddings; update usage
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          borderColor: theme.colors.grey400,
          "@media (max-width:600px)": {
            padding: "16px",
          },
          "@media (min-width:600px)": {
            padding: "24px",
          },
          boxShadow: "0px 8px 24px 0px #959DA533",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          backgroundColor: theme.colors.grey300,

          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.colors.grey500,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.colors.blue400,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.colors.red800,
          },
        },
        input: {
          padding: "16px",
          height: "24px",
        },
        multiline: {
          padding: "0",
        },

        notchedOutline: {
          borderRadius: "8px",
          borderColor: theme.colors.grey300,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: "8px 12px 0px 0px",
          "&.Mui-error": {
            color: theme.colors.red800,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${theme.colors.grey400}`,
          marginTop: "4px",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontFamily: "Inter, sans-serif",
          fontSize: "16px",
          fontWeight: 600,
          borderRadius: "1000px",
          lineHeight: "24px",
          letterSpacing: "0px",
          "@media (max-width:600px)": {
            padding: "12px 20px",
          },
          "@media (min-width:600px)": {
            padding: "16px 24px",
          },

          "&.Mui-disabled": {
            backgroundColor: theme.colors.grey400,
          },
        },
        containedPrimary: {
          backgroundColor: theme.colors.blue600,
          color: theme.colors.grey000,
          "&:hover": {
            "@media (min-width:900px)": {
              backgroundColor: theme.colors.blue700,
            },
          },
        },
        containedSecondary: {
          backgroundColor: theme.colors.grey000,
          color: theme.colors.blue600,
          "&:hover": {
            "@media (min-width:900px)": {
              backgroundColor: theme.colors.blue200,
            },
          },
        },
        containedTertiary: {
          backgroundColor: theme.colors.grey000,
          color: theme.colors.blue600,
          border: `1px solid ${theme.colors.grey400}`,
          "&:hover": {
            "@media (min-width:900px)": {
              backgroundColor: theme.colors.blue200,
            },
          },
        },
        containedError: {
          backgroundColor: theme.colors.grey000,
          color: theme.colors.red800,
          "&:hover": {
            "@media (min-width:900px)": {
              backgroundColor: theme.colors.red200,
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: theme.colors.grey400,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: theme.colors.grey200,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "12px 16px",
          border: "none",

          maxWidth: "100%",

          "&:first-of-type": {
            paddingLeft: "20px",
            borderTopLeftRadius: "8px",
            borderBottomLeftRadius: "8px",
          },
          "&:last-of-type": {
            paddingRight: "20px",
            borderTopRightRadius: "8px",
            borderBottomRightRadius: "8px",
          },
        },

        head: {
          color: theme.colors.grey700,
          ...theme.typography.subtitle1,
        },

        body: {
          padding: "20px 16px",

          "@media (max-width:1160px)": {
            padding: "20px 12px",
          },

          borderTop: `1px solid ${theme.colors.grey400}`,
          borderBottom: `1px solid ${theme.colors.grey400}`,

          "&:first-of-type": {
            borderLeft: `1px solid ${theme.colors.grey400}`,
          },

          "&:last-of-type": {
            borderRight: `1px solid ${theme.colors.grey400}`,
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: "separate",
          borderSpacing: "0 8px",
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: <CheckboxBorderIcon />,
        checkedIcon: <CheckboxCheckedIcon />,
      },
      styleOverrides: {
        root: {
          padding: "8px",
          marginRight: "4px",
          "&.Mui-checked": {
            color: theme.colors.blue600,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: "8px",
          marginRight: "4px",
          color: theme.colors.grey400,
          "&.Mui-checked": {
            color: theme.colors.blue600,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: "-8px",
        },
        label: {
          ...theme.typography.body2,
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        slotProps: {
          indicator: {
            sx: {
              display: "none",
            },
          },
        },
      },
      styleOverrides: {
        root: {
          borderRadius: "8px",
          backgroundColor: theme.colors.grey300,
          border: `4px solid ${theme.colors.grey300}`,
          minHeight: "34px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          color: theme.colors.grey900,
          backgroundColor: theme.colors.grey300,
          padding: "4px",
          minHeight: "26px",
          minWidth: "32px",
          "&.Mui-selected": {
            backgroundColor: theme.colors.grey000,
          },
          ...theme.typography.body2,
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: <AlertSuccessIcon />,
          error: <AlertErrorIcon />,
        },
      },
      styleOverrides: {
        root: {
          borderRadius: "8px",
          padding: "16px !important",
          "&.MuiAlert-standardError": {
            backgroundColor: theme.colors.red700,
            color: theme.colors.grey000,
          },
          "&.MuiAlert-standardSuccess": {
            backgroundColor: theme.colors.green800,
            color: theme.colors.grey000,
          },
        },
        icon: {
          marginRight: "8px",
          padding: "0px",
          alignItems: "center",
        },
        message: {
          padding: "0px",
          ...theme.typography.body2,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          backgroundColor: "#F5F8FF",
          textAlign: "left",
          boxShadow: "none",
          "&:before": {
            display: "none",
          },
          "@media (max-width:600px)": {
            padding: "20px !important",
          },
          "@media (min-width:600px)": {
            padding: "24px 40px !important",
          },
          marginBottom: "24px",
        },
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: (
          <Box
            sx={{
              p: "8px",
              backgroundColor: "#fff",
              borderRadius: "100px",
              width: "40px",
              height: "40px",
            }}
          >
            <ExpandIcon />
          </Box>
        ),
      },
      styleOverrides: {
        root: {
          padding: "0",
          minHeight: "0",

          "&.Mui-expanded": {
            minHeight: "0",
          },
        },
        content: {
          margin: "0",
          marginRight: "20px",

          "&.Mui-expanded": {
            margin: "0 20px 0 0",
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "0",
          margin: "12px 0 0 0",
          "@media (max-width:600px)": {
            marginRight: "35px",
          },
          "@media (min-width:600px)": {
            marginRight: "60px",
          },
          color: "#0A0B0D",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: "100px",
          height: "8px",
          backgroundColor: theme.colors.blue200,
          "&.MuiLinearProgress-colorPrimary": {
            backgroundColor: theme.colors.blue200,
            "&.MuiLinearProgress-bar": {
              backgroundColor: theme.colors.blue600,
              borderRadius: "100px",
            },
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: "12px 16px",
          margin: "0",
          marginBottom: "8px",
          borderRadius: "8px",
          border: `1px solid ${theme.colors.grey400}`,

          "&:last-of-type": {
            marginBottom: "0",
          },
        },
      },
    },
    MuiPickersToolbarButton: {
      styleOverrides: {
        root: {
          padding: "0px !important",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          ...theme.typography.body2,
        },
        colorSuccess: {
          backgroundColor: theme.colors.green300,
          color: theme.colors.green800,
        },
        colorError: {
          backgroundColor: theme.colors.red200,
          color: theme.colors.red800,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: SelectExpandIcon,
      },
      styleOverrides: {
        select: {
          paddingRight: "40px !important",
        },
        icon: {
          right: "16px",
        },
      },
    },
  },
});

export default theme;
